import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Star, Languages, Award, Filter, ChevronDown, ChevronUp, SortAsc, Heart, MessageSquare, ArrowLeft, Clock, Users, Shield, RefreshCw, Camera, Coffee } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTranslation } from 'react-i18next';

interface Guide {
  id: number;
  name: string;
  age: number;
  nationality: string;
  location: string;
  languages: string[];
  experience: number;
  rating: number;
  reviews: number;
  specialties: string[];
  image: string;
  bio: string;
  isSuperguide: boolean;
  verified: boolean;
  joinedDate: string;
  completedTours: number;
}

const guides: Guide[] = [
  {
    id: 1,
    name: 'Agaba Mathius',
    age: 32,
    nationality: 'Ugandan',
    location: 'Bwindi, Uganda',
    languages: ['English', 'Swahili', 'Rukiga'],
    experience: 8,
    rating: 4.9,
    reviews: 127,
    specialties: ['Gorilla Trekking', 'Wildlife Photography'],
    image: 'https://imgur.com/56lSR2X.jpg',
    bio: 'Experienced wildlife guide specializing in gorilla trekking and photography tours in Bwindi Impenetrable Forest.',
    isSuperguide: true,
    verified: true,
    joinedDate: '2015',
    completedTours: 450
  },
  {
    id: 2,
    name: 'Elisa Lule',
    age: 28,
    nationality: 'Ugandan',
    location: 'Kampala, Uganda',
    languages: ['English', 'Luganda'],
    experience: 5,
    rating: 4.8,
    reviews: 93,
    specialties: ['City Tours', 'Cultural Experiences'],
    image: 'https://imgur.com/zG8RjKq.jpg',
    bio: 'Cultural expert offering immersive city tours and authentic local experiences in Kampala.',
    isSuperguide: true,
    verified: true,
    joinedDate: '2018',
    completedTours: 280
  },
  {
    id: 3,
    name: 'Ankunda Prima',
    age: 35,
    nationality: 'Ugandan',
    location: 'Multiple Locations',
    languages: ['English', 'Luganda', 'Runyankole'],
    specialties: ['Self-Drive Safaris', 'Adventure Tours', 'Jinja Night Crawling'],
    experience: 6,
    rating: 4.7,
    reviews: 85,
    image: 'https://imgur.com/weW9NxW.jpg',
    bio: 'Self-drive safari specialist with extensive knowledge of Uganda\'s road networks and hidden gems. Also offers exciting Jinja night crawling experiences.',
    isSuperguide: false,
    verified: true,
    joinedDate: '2013',
    completedTours: 520
  },
  {
    id: 4,
    name: 'Niwagaba Esser',
    age: 29,
    nationality: 'Ugandan',
    location: 'Kibale Forest, Uganda',
    languages: ['English', 'Swahili'],
    experience: 4,
    rating: 4.8,
    reviews: 76,
    specialties: ['Chimpanzee Tracking', 'Forest Walks'],
    image: 'https://imgur.com/HlCFFey.jpg',
    bio: 'Specialized in chimpanzee tracking and primate experiences in Kibale Forest National Park.',
    isSuperguide: false,
    verified: true,
    joinedDate: '2019',
    completedTours: 180
  },
  {
    id: 5,
    name: 'Farouk Ssendaire',
    age: 38,
    nationality: 'Ugandan',
    location: 'Queen Elizabeth, Uganda',
    languages: ['English', 'Swahili', 'Runyankole'],
    experience: 5,
    rating: 4.8,
    reviews: 104,
    specialties: ['Wildlife Safaris', 'Bird Watching'],
    image: 'https://imgur.com/5I0fk6J.jpg',
    bio: 'Veteran safari guide with expertise in wildlife tracking and bird identification.',
    isSuperguide: true,
    verified: true,
    joinedDate: '2011',
    completedTours: 720
  },
  {
    id: 7,
    name: 'Enoch',
    age: 34,
    nationality: 'Ugandan',
    location: 'Kidepo Valley, Uganda',
    languages: ['English', 'Swahili', 'Karamojong'],
    experience: 7,
    rating: 4.9,
    reviews: 42,
    specialties: ['Remote Safaris', 'Photography'],
    image: 'https://imgur.com/yDiQEtu.jpg',
    bio: 'Remote safari specialist with extensive knowledge of Kidepo Valley National Park.',
    isSuperguide: true,
    verified: true,
    joinedDate: '2014',
    completedTours: 320
  },
  {
    id: 9,
    name: 'Azel John Makumbi',
    age: 31,
    nationality: 'Ugandan',
    location: 'Kampala, Uganda',
    languages: ['English', 'Luganda'],
    specialties: ['Kampala City Tours', 'Kampala Night Life', 'Underground Scene'],
    experience: 4,
    rating: 4.8,
    reviews: 87,
    image: 'https://imgur.com/bCDzSxZ.jpg',
    bio: 'Specialized in Kampala city tours and nightlife experiences, showing visitors the vibrant urban culture of Uganda\'s capital.',
    isSuperguide: false,
    verified: true,
    joinedDate: '2021',
    completedTours: 145
  },
  {
    id: 10,
    name: 'Emmy Frass',
    age: 29,
    nationality: 'Ugandan',
    location: 'Kampala, Uganda',
    languages: ['English', 'Luganda', 'Swahili'],
    specialties: ['Kampala Night Crawling', 'Nightlife Guide', 'Club VIP Access'],
    experience: 5,
    rating: 4.9,
    reviews: 94,
    image: 'https://imgur.com/Uo9g1J3.jpg',
    bio: 'Expert nightlife guide specializing in Kampala night crawling experiences, club tours, and the best entertainment spots in the city.',
    isSuperguide: false,
    verified: true,
    joinedDate: '2020',
    completedTours: 210
  },
  {
    id: 11,
    name: 'Lilian Kabagenyi',
    age: 27,
    nationality: 'Ugandan',
    location: 'Fort Portal, Uganda',
    languages: ['English', 'Rutooro', 'Swahili'],
    specialties: ['Adventure Tours', 'Hiking', 'Cultural Experiences'],
    experience: 4,
    rating: 4.8,
    reviews: 68,
    image: 'https://imgur.com/4UlcwK3.jpg',
    bio: 'Adventure tour guide specializing in hiking expeditions and cultural immersion experiences in the Fort Portal region and Rwenzori Mountains.',
    isSuperguide: false,
    verified: true,
    joinedDate: '2021',
    completedTours: 175
  }
];

const TravelGuides = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('rating');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const allSpecialties = Array.from(new Set(guides.flatMap(guide => guide.specialties)));
  const allLanguages = Array.from(new Set(guides.flatMap(guide => guide.languages)));

  const filteredGuides = guides.filter(guide => {
    const matchesSearch = guide.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         guide.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         guide.nationality.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSpecialties = selectedSpecialties.length === 0 ||
                              selectedSpecialties.some(specialty => guide.specialties.includes(specialty));
    
    const matchesLanguages = selectedLanguages.length === 0 ||
                            selectedLanguages.some(language => guide.languages.includes(language));
    
    return matchesSearch && matchesSpecialties && matchesLanguages;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'experience':
        return b.experience - a.experience;
      case 'reviews':
        return b.reviews - a.reviews;
      default:
        return 0;
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsLoading(false), 800);
  }, []);

  return (
    <>
      <Navbar />
      
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2572&q=80"
            alt="Uganda landscape"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('pages.travelGuides.title')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('pages.travelGuides.subtitle')}
            </p>
            
            <div className="relative max-w-xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 block w-full border border-gray-300 rounded-lg bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 focus:ring-ta-green focus:border-ta-green"
              />
            </div>
          </div>
        </div>
      </section>
      
      <section className="bg-white border-b border-gray-200 sticky top-14 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('pages.travelGuides.filters')}
                {isFilterOpen ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </button>
              
              <div className="hidden md:flex items-center space-x-2">
                <span className="text-sm text-gray-500">{t('pages.travelGuides.sortBy')}:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green"
                >
                  <option value="rating">{t('common.rating')}</option>
                  <option value="experience">{t('pages.guideProfile.experience')}</option>
                  <option value="reviews">{t('pages.guideProfile.reviews')}</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
          
          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.guideProfile.specialties')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {allSpecialties.map((specialty) => (
                      <button
                        key={specialty}
                        onClick={() => {
                          if (selectedSpecialties.includes(specialty)) {
                            setSelectedSpecialties(selectedSpecialties.filter(s => s !== specialty));
                          } else {
                            setSelectedSpecialties([...selectedSpecialties, specialty]);
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedSpecialties.includes(specialty)
                            ? 'bg-ta-green text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {specialty}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.guideProfile.languages')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {allLanguages.map((language) => (
                      <button
                        key={language}
                        onClick={() => {
                          if (selectedLanguages.includes(language)) {
                            setSelectedLanguages(selectedLanguages.filter(l => l !== language));
                          } else {
                            setSelectedLanguages([...selectedLanguages, language]);
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedLanguages.includes(language)
                            ? 'bg-ta-green text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {language}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {filteredGuides.length} {t('pages.travelGuides.title').toLowerCase()}
            </h2>
          </div>
          
          {filteredGuides.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('pages.allExperiences.noResults')}</h3>
              <p className="text-gray-600 mb-4">{t('pages.allExperiences.resetFilters')}</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedSpecialties([]);
                  setSelectedLanguages([]);
                }}
                className="px-4 py-2 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors"
              >
                {t('pages.allExperiences.resetFilters')}
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredGuides.map((guide) => (
                <Link
                  key={guide.id}
                  to={`/guides/${guide.id}`}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="relative h-40">
                    <img 
                      src={guide.image} 
                      alt={guide.name} 
                      className="w-full h-full object-cover"
                    />
                    {guide.isSuperguide && (
                      <div className="absolute top-2 left-2 bg-ta-green text-white text-[8px] px-1.5 py-0.5 rounded-full flex items-center">
                        <Award className="h-2 w-2 mr-0.5" />
                        Superguide
                      </div>
                    )}
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        // Add to favorites functionality
                      }}
                      className="absolute top-2 right-2 p-1 bg-white/80 rounded-full hover:bg-white transition-colors"
                    >
                      <Heart className="h-3 w-3 text-gray-600 hover:text-red-500" />
                    </button>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{guide.name}</h3>
                      <div className="flex items-center bg-ta-green/10 rounded-full px-1 py-0.5">
                        <Star className="h-2 w-2 text-ta-green fill-current" />
                        <span className="ml-0.5 text-xs font-medium text-ta-green">{guide.rating}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-xs text-gray-600 mb-1">
                      <MapPin className="h-3 w-3 text-ta-green mr-0.5" />
                      <span className="truncate">{guide.location}</span>
                    </div>
                    
                    <div className="flex items-center text-xs text-gray-600 mb-1">
                      <Languages className="h-3 w-3 text-ta-green mr-0.5" />
                      <span className="truncate">{guide.languages.slice(0, 2).join(', ')}{guide.languages.length > 2 ? '...' : ''}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-2">
                      {guide.specialties.slice(0, 1).map((specialty, index) => (
                        <span 
                          key={index}
                          className="px-1 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[8px]"
                        >
                          {specialty}
                        </span>
                      ))}
                      {guide.specialties.length > 1 && (
                        <span className="px-1 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[8px]">
                          +{guide.specialties.length - 1}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center text-gray-600">
                        <MessageSquare className="h-3 w-3 mr-0.5" />
                        <span>{guide.reviews}</span>
                      </div>
                      <span className="text-ta-green group-hover:underline text-[10px]">
                        {t('pages.travelGuides.viewProfile')}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGuides.map((guide) => (
                <Link
                  key={guide.id}
                  to={`/guides/${guide.id}`}
                  className="block bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex">
                    <div className="w-40 h-40 flex-shrink-0">
                      <img 
                        src={guide.image} 
                        alt={guide.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">{guide.name}</h3>
                          <div className="flex items-center text-xs text-gray-600">
                            <MapPin className="h-3 w-3 text-ta-green mr-1" />
                            {guide.location}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center bg-ta-green/10 rounded-full px-2 py-0.5">
                            <Star className="h-3 w-3 text-ta-green fill-current" />
                            <span className="ml-0.5 text-xs font-medium text-ta-green">{guide.rating}</span>
                          </div>
                          {guide.isSuperguide && (
                            <div className="bg-ta-green text-white px-2 py-0.5 rounded-full flex items-center text-xs">
                              <Award className="h-3 w-3 mr-0.5" />
                              Superguide
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-600 mb-2 line-clamp-2">{guide.bio}</p>
                      
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <div>
                          <div className="text-xs text-gray-600">{t('pages.guideProfile.experience')}</div>
                          <div className="text-xs font-medium">{guide.experience} {t('common.years')}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600">{t('pages.guideProfile.tours')}</div>
                          <div className="text-xs font-medium">{guide.completedTours}+</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600">{t('pages.guideProfile.languages')}</div>
                          <div className="text-xs font-medium truncate">{guide.languages.join(', ')}</div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mb-2">
                        {guide.specialties.slice(0, 3).map((specialty, index) => (
                          <span 
                            key={index}
                            className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs"
                          >
                            {specialty}
                          </span>
                        ))}
                        {guide.specialties.length > 3 && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                            +{guide.specialties.length - 3}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-xs text-gray-600">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {guide.reviews} {t('common.reviews')}
                        </div>
                        <span className="text-ta-green text-xs group-hover:underline">
                          {t('pages.travelGuides.viewProfile')}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default TravelGuides;